/* ============================================================
   EVIA THEME — Product Page Specific
   Gallery · Live visitor · Sticky ATC · Quantity · Pixel events
   ============================================================ */

(function() {
  'use strict';

  // ==================== IMAGE / VIDEO GALLERY ====================
  const mainImg = document.getElementById('mainImg');
  const galleryMain = document.querySelector('.gallery-main');
  const galleryThumbs = Array.from(document.querySelectorAll('.gallery-thumb'));
  // Lazily created once the first video thumb is activated — until then, zero video
  // bytes (not even metadata) are requested.
  let galleryVideo = null;

  // Declared as a function so it can be referenced (hoisted) from showVideoThumb below,
  // even though it's implemented as part of the auto-advance block further down.
  function handleGalleryVideoEnded() {
    scheduleAdvance();
  }

  function showVideoThumb(thumb) {
    const videoSrc = thumb.dataset.videoSrc;
    if (!videoSrc || !galleryMain || !mainImg) return;
    if (!galleryVideo) {
      galleryVideo = document.createElement('video');
      galleryVideo.controls = true;
      galleryVideo.playsInline = true;
      galleryVideo.preload = 'none';
      galleryVideo.addEventListener('ended', handleGalleryVideoEnded);
      galleryMain.appendChild(galleryVideo);
    }
    galleryVideo.poster = thumb.dataset.videoPoster || '';
    if (galleryVideo.getAttribute('src') !== videoSrc) galleryVideo.src = videoSrc;
    mainImg.style.display = 'none';
    galleryVideo.style.display = '';
  }

  function showImageThumb(thumb) {
    const src = thumb.dataset.src || thumb.querySelector('img')?.src;
    if (!src || !mainImg) return;
    if (galleryVideo) {
      galleryVideo.pause();
      galleryVideo.style.display = 'none';
    }
    mainImg.style.display = '';
    mainImg.src = src;
  }

  // Shared by manual clicks and the auto-advance timer so both paths behave identically.
  function activateThumb(thumb) {
    if (thumb.classList.contains('gallery-thumb--video')) {
      showVideoThumb(thumb);
      // Pause auto-advance while the video plays; it resumes on 'ended' (or immediately
      // if autoplay gets blocked, via the catch below).
      clearAdvanceTimer();
      if (galleryVideo) {
        // Muted is required — browsers block autoplay with sound. Controls stay
        // visible so the viewer can unmute.
        galleryVideo.muted = true;
        const playPromise = galleryVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { scheduleAdvance(); });
        }
      } else {
        // No usable mp4 source, so showVideoThumb bailed and created nothing.
        // Keep the slideshow moving rather than stalling on a dead thumb.
        scheduleAdvance();
      }
    } else {
      showImageThumb(thumb);
      scheduleAdvance();
    }
    galleryThumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    preloadNext(thumb);
  }

  galleryThumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      // A manual click ends the slideshow for good — never fight the user.
      stopAutoAdvance();
      activateThumb(thumb);
    });
  });

  // ==================== GALLERY AUTO-ADVANCE SLIDESHOW ====================
  const GALLERY_ADVANCE_MS = 5000;
  const galleryHasMultipleThumbs = galleryThumbs.length > 1;
  const galleryReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const galleryAutoAdvanceAllowed = galleryHasMultipleThumbs && !galleryReducedMotion;
  let galleryAdvanceTimer = null;
  let galleryAutoAdvanceStopped = false;

  function clearAdvanceTimer() {
    if (galleryAdvanceTimer) {
      clearTimeout(galleryAdvanceTimer);
      galleryAdvanceTimer = null;
    }
  }

  function scheduleAdvance() {
    if (!galleryAutoAdvanceAllowed || galleryAutoAdvanceStopped || document.hidden) return;
    clearAdvanceTimer();
    galleryAdvanceTimer = setTimeout(advanceToNext, GALLERY_ADVANCE_MS);
  }

  function stopAutoAdvance() {
    galleryAutoAdvanceStopped = true;
    clearAdvanceTimer();
  }

  function advanceToNext() {
    if (galleryAutoAdvanceStopped) return;
    const active = document.querySelector('.gallery-thumb.active') || galleryThumbs[0];
    const idx = galleryThumbs.indexOf(active);
    const next = galleryThumbs[(idx + 1) % galleryThumbs.length];
    if (next) activateThumb(next);
  }

  // Preload the next image thumb's full-size src so the upcoming swap never flashes.
  // Video thumbs are skipped on purpose — that would defeat the lazy-video loading.
  function preloadNext(thumb) {
    if (!galleryHasMultipleThumbs) return;
    const idx = galleryThumbs.indexOf(thumb);
    if (idx === -1) return;
    const next = galleryThumbs[(idx + 1) % galleryThumbs.length];
    if (next && !next.classList.contains('gallery-thumb--video') && next.dataset.src) {
      new Image().src = next.dataset.src;
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearAdvanceTimer();
    } else if (galleryAutoAdvanceAllowed && !galleryAutoAdvanceStopped) {
      // Don't resume the timer out from under a video that's still playing —
      // handleGalleryVideoEnded will resume it once the video finishes.
      if (!galleryVideo || galleryVideo.paused || galleryVideo.ended) {
        scheduleAdvance();
      }
    }
  });

  // Preload the image that the first advance will land on, so even that first
  // swap is instant.
  const galleryInitialThumb = document.querySelector('.gallery-thumb.active') || galleryThumbs[0];
  if (galleryInitialThumb) preloadNext(galleryInitialThumb);

  scheduleAdvance();

  // ==================== QUANTITY ====================
  const qtyDisplay = document.getElementById('qtyDisplay');
  const qtyInput = document.getElementById('qtyInput');
  document.getElementById('qtyMinus')?.addEventListener('click', () => {
    const q = parseInt(qtyDisplay?.textContent || 1, 10);
    if (q > 1) {
      qtyDisplay.textContent = q - 1;
      if (qtyInput) qtyInput.value = q - 1;
    }
  });
  document.getElementById('qtyPlus')?.addEventListener('click', () => {
    const q = parseInt(qtyDisplay?.textContent || 1, 10);
    qtyDisplay.textContent = q + 1;
    if (qtyInput) qtyInput.value = q + 1;
  });

  // ==================== STICKY ATC (Intersection Observer) ====================
  const mainATC = document.getElementById('mainATC');
  const stickyATC = document.getElementById('stickyATC');
  if (mainATC && stickyATC && 'IntersectionObserver' in window) {
    document.body.classList.add('has-sticky-atc');
    const obs = new IntersectionObserver(([entry]) => {
      stickyATC.classList.toggle('visible', !entry.isIntersecting);
    }, { threshold: 0 });
    obs.observe(mainATC);
  }

  // Sync sticky ATC click to main form
  document.getElementById('stickyATCBtn')?.addEventListener('click', () => {
    const form = document.getElementById('productForm');
    form?.requestSubmit();
  });

  // The live visitor counter used to run a second, independent random walk here, which
  // is how the pill ended up claiming more shoppers than the page claimed viewers.
  // Both numbers now come from the single ticker in evia.js.

  // ==================== PIXEL: ViewContent ====================
  if (typeof fbq !== 'undefined') {
    const pdpData = document.getElementById('pdpPixelData');
    if (pdpData) {
      try {
        const data = JSON.parse(pdpData.textContent);
        fbq('track', 'ViewContent', data);
      } catch (e) {}
    }
  }

  // ==================== LAZY-LOAD JUDGE.ME REVIEWS ====================
  const reviewSection = document.querySelector('.product-reviews');
  if (reviewSection && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries, observer) => {
      if (entries[0].isIntersecting) {
        // Placeholder — replace with actual Judge.me script when key is available
        // const s = document.createElement('script');
        // s.src = 'https://cdn.judge.me/...';
        // document.head.appendChild(s);
        observer.disconnect();
      }
    }, { rootMargin: '300px' }).observe(reviewSection);
  }
})();
